export type DeteriorationListLocationNode = {
  id?: string | null;
  locationId?: string | null;
  key?: string | null;
  value?: string | null;
  name?: string | null;
  locationName?: string | null;
  title?: string | null;
  label?: string | null;
  children?: DeteriorationListLocationNode[] | null;
};

export type DeteriorationListCategoryRecord = {
  id?: string | null;
  categoryLabel?: string | null;
  categoryKey?: string | null;
  name?: string | null;
  label?: string | null;
};

type ResolveCategoryLabelInput = {
  equipmentCategoryId?: string | null;
  categoryInfo?: unknown;
  categoryLabel?: unknown;
  legacyCategory?: unknown;
  categoryLabelById: Map<string, string>;
};

type ResolveLocationNameInput = {
  locationId?: string | null;
  location?: unknown;
  locationName?: unknown;
  legacyLocation?: unknown;
  locationNameById: Map<string, string>;
};

type ResolveMaintenanceRecommendationInput = {
  currentRate: number | null;
  predictedRate?: number | null;
  maintenancePriority?: string | null;
  maintenanceRecommendation?: string | null;
};

const FALLBACK_LOCATION = '未設定';
const FALLBACK_CATEGORY = '未設定';
export const PREDICTION_DATA_REQUIRED_LABEL = '要データ設定';
export const PREDICTION_DATA_REQUIRED_MESSAGE = '予測のためのデータを設定してください';

const trimString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const resolveRecommendationByPriority = (priority: string): string | null => {
  switch (priority.toLowerCase()) {
    case 'critical':
    case 'urgent':
      return '緊急点検と交換計画の策定';
    case 'high':
      return '詳細点検を実施してください';
    case 'medium':
    case 'warning':
      return '点検頻度を上げて監視してください';
    case 'low':
    case 'normal':
      return '定期監視を継続してください';
    default:
      return null;
  }
};

export const createLocationNameMap = (
  roots: DeteriorationListLocationNode[] | null | undefined,
): Map<string, string> => {
  const result = new Map<string, string>();
  if (!Array.isArray(roots) || roots.length === 0) {
    return result;
  }

  const walk = (node: DeteriorationListLocationNode): void => {
    const id =
      trimString(node.id) ??
      trimString(node.locationId) ??
      trimString(node.key) ??
      trimString(node.value);
    const name =
      trimString(node.name) ??
      trimString(node.locationName) ??
      trimString(node.title) ??
      trimString(node.label);
    if (id && name) {
      result.set(id, name);
    }

    if (!Array.isArray(node.children)) {
      return;
    }

    for (const child of node.children) {
      walk(child);
    }
  };

  for (const node of roots) {
    walk(node);
  }

  return result;
};

export const createCategoryLabelMap = (
  categories: DeteriorationListCategoryRecord[] | null | undefined,
): Map<string, string> => {
  const result = new Map<string, string>();
  if (!Array.isArray(categories) || categories.length === 0) {
    return result;
  }

  for (const category of categories) {
    const id = trimString(category.id);
    const label =
      trimString(category.categoryLabel) ??
      trimString(category.label) ??
      trimString(category.name) ??
      trimString(category.categoryKey);
    if (id && label) {
      result.set(id, label);
    }
  }

  return result;
};

export const resolveLocationName = ({
  locationId,
  location,
  locationName,
  legacyLocation,
  locationNameById,
}: ResolveLocationNameInput): string => {
  const normalizedLocationId = trimString(locationId);
  if (normalizedLocationId) {
    const mappedLocationName = locationNameById.get(normalizedLocationId);
    if (mappedLocationName) {
      return mappedLocationName;
    }
  }

  if (location && typeof location === 'object') {
    const locationObject = location as Record<string, unknown>;
    const objectLocationId =
      trimString(locationObject.id) ??
      trimString(locationObject.locationId) ??
      trimString(locationObject.key) ??
      trimString(locationObject.value);
    if (objectLocationId) {
      const mappedLocationName = locationNameById.get(objectLocationId);
      if (mappedLocationName) {
        return mappedLocationName;
      }
    }
    const objectLocationName =
      trimString(locationObject.name) ??
      trimString(locationObject.locationName) ??
      trimString(locationObject.title) ??
      trimString(locationObject.label);
    if (objectLocationName) {
      return objectLocationName;
    }
  }

  const directLocationName = trimString(locationName) ?? trimString(legacyLocation);
  if (directLocationName) {
    return directLocationName;
  }
  return FALLBACK_LOCATION;
};

export const resolveCategoryLabel = ({
  equipmentCategoryId,
  categoryInfo,
  categoryLabel,
  legacyCategory,
  categoryLabelById,
}: ResolveCategoryLabelInput): string => {
  const normalizedCategoryId = trimString(equipmentCategoryId);
  if (normalizedCategoryId) {
    const labelFromId = categoryLabelById.get(normalizedCategoryId);
    if (labelFromId) {
      return labelFromId;
    }
  }

  if (categoryInfo && typeof categoryInfo === 'object') {
    const categoryObject = categoryInfo as Record<string, unknown>;
    const objectCategoryId =
      trimString(categoryObject.id) ??
      trimString(categoryObject.categoryId) ??
      trimString(categoryObject.equipmentCategoryId);
    if (objectCategoryId) {
      const labelFromObjectId = categoryLabelById.get(objectCategoryId);
      if (labelFromObjectId) {
        return labelFromObjectId;
      }
    }
    const objectLabel =
      trimString(categoryObject.categoryLabel) ??
      trimString(categoryObject.categoryKey) ??
      trimString(categoryObject.name) ??
      trimString(categoryObject.label) ??
      trimString(categoryObject.title);
    if (objectLabel) {
      return objectLabel;
    }
  }

  const normalizedCategoryLabel = trimString(categoryLabel);
  if (normalizedCategoryLabel) {
    return normalizedCategoryLabel;
  }

  const legacyString = trimString(legacyCategory);
  if (legacyString) {
    return legacyString;
  }

  if (legacyCategory && typeof legacyCategory === 'object') {
    const categoryObject = legacyCategory as {
      categoryLabel?: unknown;
      categoryKey?: unknown;
      name?: unknown;
    };
    const objectLabel =
      trimString(categoryObject.categoryLabel) ??
      trimString(categoryObject.categoryKey) ??
      trimString(categoryObject.name);
    if (objectLabel) {
      return objectLabel;
    }
  }

  return FALLBACK_CATEGORY;
};

export const resolveMaintenanceRecommendation = ({
  currentRate,
  predictedRate,
  maintenancePriority,
  maintenanceRecommendation,
}: ResolveMaintenanceRecommendationInput): string => {
  const recommendationFromPayload = trimString(maintenanceRecommendation);
  if (recommendationFromPayload) {
    return recommendationFromPayload;
  }

  const normalizedPriority = trimString(maintenancePriority);
  if (normalizedPriority) {
    const recommendationFromPriority = resolveRecommendationByPriority(normalizedPriority);
    if (recommendationFromPriority) {
      return recommendationFromPriority;
    }
  }

  const recommendationRate = currentRate ?? predictedRate ?? null;
  if (recommendationRate === null) {
    return PREDICTION_DATA_REQUIRED_LABEL;
  }

  if (recommendationRate >= 85) {
    return '緊急点検と交換計画の策定';
  }
  if (recommendationRate >= 70) {
    return '詳細点検を実施してください';
  }
  if (recommendationRate >= 50) {
    return '点検頻度を上げて監視してください';
  }
  return '定期監視を継続してください';
};
